import { staffClient } from 'clients/staff';

/**
 * 현장 구분 (감리/공사/설계)
 * - 서버: site.category (enum, 기본값 SUPERVISION)
 * - 목록 조회 시 `GET /site?category=...` 로 필터 가능
 */
export type SiteCategory = 'SUPERVISION' | 'CONSTRUCTION' | 'DESIGN';

export const SITE_CATEGORIES: SiteCategory[] = ['SUPERVISION', 'CONSTRUCTION', 'DESIGN'];

export const SITE_CATEGORY_LABEL: Record<SiteCategory, string> = {
    SUPERVISION: '감리',
    CONSTRUCTION: '공사',
    DESIGN: '설계',
};

/**
 * 구분은 "상태"가 아니라 분류이므로 시맨틱 색(success/warning/danger)을 쓰지 않는다.
 * DESIGN.md §2-2 — 시맨틱 색은 완료/주의/오류 의미 전용.
 * 브랜드(navy) · 무채색 · 아웃라인 3단계로만 구별한다.
 */
export const SITE_CATEGORY_BADGE: Record<SiteCategory, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
    SUPERVISION: { label: '감리', variant: 'default' },
    CONSTRUCTION: { label: '공사', variant: 'secondary' },
    DESIGN: { label: '설계', variant: 'outline' },
};

export const SITE_CATEGORY_OPTIONS: { value: SiteCategory; label: string }[] = SITE_CATEGORIES.map(
    (value) => ({ value, label: SITE_CATEGORY_LABEL[value] })
);

export const isSiteCategory = (value?: string | null): value is SiteCategory =>
    !!value && (SITE_CATEGORIES as string[]).includes(value);

/** 감리 시작/종료일의 출처 — MANUAL: 현장정보 직접 입력, ASSIGNMENT: 감리원 배치 계산 */
export type SupervisionDateSource = 'MANUAL' | 'ASSIGNMENT';

/** 현장정보에서 직접 입력하는 감리 일정 (서버 원본 필드) */
export interface SiteSupervisionSchedule {
    /** 감리 시작신고일 */
    supervisionReportDate?: string | null;
    /** 감리원 투입일 */
    supervisionDeployDate?: string | null;
    /** 감리 종료일 */
    supervisionFinishDate?: string | null;
}

/** 조회 응답에만 들어오는 파생 필드 (저장 시 보내지 않는다) */
export interface SiteSupervisionDerived {
    /** 입력값이 있으면 그 값, 없으면 감리원 배치에서 계산 */
    supervisionStartDate?: string | null;
    supervisionEndDate?: string | null;
    supervisionDateSource?: SupervisionDateSource;
}

export const getSites = async (offset: number = 0, limit: number = 100, category?: SiteCategory) => {
    try {
        const response = await staffClient.get('/site', {
            params: {
                offset,
                limit,
                ...(category ? { category } : {})
            }
        });
        return response;
    } catch (error) {
        console.error('Error fetching sites:', error);
        throw error;
    }
};

export const getSiteById = async (id: number) => {
    try {
        const response = await staffClient.get(`/site/${id}`);
        return response;
    } catch (error) {
        console.error('Error fetching site by id:', error);
        throw error;
    }
};

export const createSite = async (siteData: any) => {
    try {
        const response = await staffClient.post('/site', siteData);
        return response;
    } catch (error) {
        console.error('Error creating site:', error);
        throw error;
    }
};

export const updateSite = async (id: number, siteData: any) => {
    try {
        const response = await staffClient.patch(`/site/${id}`, siteData);
        return response;
    } catch (error) {
        console.error('Error updating site:', error);
        throw error;
    }
};

export const deleteSite = async (id: number) => {
    try {
        const response = await staffClient.delete(`/site/${id}`);
        return response;
    } catch (error) {
        console.error('Error deleting site:', error);
        throw error;
    }
};